import * as React from 'react'
import { sb } from '@/lib/supabase'
import type { Team, TeamStatus } from '@/lib/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useDirty, useDiscardGuard } from '@/hooks/use-unsaved'
import { DiscardPrompt } from '@/components/discard-prompt'

/**
 * Create or edit a team. Shared by Mission Control and Admin so the two can't
 * drift on validation or on which columns a team actually has.
 *
 * `onSaved` receives the saved row — Mission Control uses it to switch straight
 * into a team it just created.
 */
export function TeamDialog({
  team,
  onClose,
  onSaved,
}: {
  team?: Team
  onClose: () => void
  onSaved: (saved: Team) => void
}) {
  const [name, setName] = React.useState(team?.name ?? '')
  const [slug, setSlug] = React.useState(team?.slug ?? '')
  const [company, setCompany] = React.useState(team?.company ?? '')
  const [description, setDescription] = React.useState(team?.description ?? '')
  const [startedOn, setStartedOn] = React.useState(team?.started_on ?? '')
  const [endsOn, setEndsOn] = React.useState(team?.ends_on ?? '')
  const [sprintDays, setSprintDays] = React.useState(String(team?.sprint_length_days ?? 14))
  const [status, setStatus] = React.useState<TeamStatus>(team?.status ?? 'active')
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const dirty = useDirty({ name, slug, company, description, startedOn, endsOn, sprintDays, status })
  const guard = useDiscardGuard(dirty, onClose)

  const slugTouched = React.useRef(Boolean(team))

  async function save(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) return setError('Give the team a name.')
    const s = (slug.trim() || slugify(name)).toLowerCase()
    const days = Number(sprintDays)
    if (!Number.isInteger(days) || days < 1 || days > 90) {
      return setError('Sprint length must be a whole number of days between 1 and 90.')
    }
    if (startedOn && endsOn && endsOn < startedOn) {
      return setError('The end date is before the start date.')
    }

    setBusy(true)
    setError(null)
    try {
      const row = {
        name: name.trim(),
        slug: s,
        company: company.trim() || null,
        description: description.trim() || null,
        started_on: startedOn || null,
        ends_on: endsOn || null,
        sprint_length_days: days,
        status,
      }
      // Return the row either way: the caller may need the new team's id, and
      // an edit still wants the server's view (updated_at, trimmed values).
      const { data, error: err } = team
        ? await sb().from('teams').update(row).eq('id', team.id).select().single()
        : await sb().from('teams').insert(row).select().single()
      if (err) throw err
      onSaved(data as Team)
    } catch (err) {
      const raw = err instanceof Error ? err.message : String(err)
      setError(/duplicate|unique/i.test(raw) ? `The slug “${s}” is already taken.` : raw)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && guard.requestClose()}>
      <DialogContent className="sm:max-w-lg">
        <DiscardPrompt guard={guard} what="this team" />
        <form onSubmit={save}>
          <DialogHeader>
            <DialogTitle>{team ? 'Edit team' : 'New team'}</DialogTitle>
            <DialogDescription>
              One team per partner engagement. Everything in the portal is scoped to a team.
            </DialogDescription>
          </DialogHeader>

          <div className="my-4 space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="tname">Team name</Label>
                <Input
                  id="tname"
                  autoFocus
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value)
                    if (!slugTouched.current) setSlug(slugify(e.target.value))
                  }}
                  placeholder="Acme Platform Team"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tslug">Slug</Label>
                <Input
                  id="tslug"
                  value={slug}
                  onChange={(e) => {
                    slugTouched.current = true
                    setSlug(e.target.value)
                  }}
                  placeholder="acme-platform"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tcompany">Company</Label>
              <Input id="tcompany" value={company} onChange={(e) => setCompany(e.target.value)} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tdesc">Description</Label>
              <Textarea id="tdesc" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="tstart">Started</Label>
                <Input id="tstart" type="date" value={startedOn} onChange={(e) => setStartedOn(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tend">Ends</Label>
                <Input id="tend" type="date" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="tsprint">Sprint length (days)</Label>
                <Input
                  id="tsprint"
                  inputMode="numeric"
                  value={sprintDays}
                  onChange={(e) => setSprintDays(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tstatus">Status</Label>
                <Select value={status} onValueChange={(v) => setStatus(v as TeamStatus)}>
                  <SelectTrigger id="tstatus">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="paused">Paused</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={guard.requestClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {team ? 'Save changes' : 'Create team'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50)
}
